import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, ShieldCheck, TrendingUp, CreditCard, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = {
  score_drop: AlertTriangle,
  new_risk: AlertTriangle,
  improvement: TrendingUp,
  billing: CreditCard,
  verified: CheckCircle2,
  welcome: ShieldCheck,
};
const COLORS = {
  critical: "#EF4444", high: "#F97316", medium: "#EAB308", low: "#10B981",
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const loadCount = useCallback(
    () => api.get("/notifications/unread_count").then((r) => setUnread(r.data.count)).catch(() => {}),
    []
  );
  const loadItems = useCallback(
    () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {}),
    []
  );

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadItems();
  };

  const markAll = async () => {
    await api.post("/notifications/read_all");
    setUnread(0);
    loadItems();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={toggle}
        className="relative p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
      >
        <Bell size={20} strokeWidth={1.75} />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-cyanbrand text-slate-900 text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-[#0C1424] border border-slate-700 rounded-lg shadow-2xl z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <span className="font-heading font-semibold text-white text-sm">Notifications</span>
            {items.length > 0 && (
              <button onClick={markAll} className="text-xs text-cyanbrand hover:underline">Mark all read</button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-slate-500 text-sm">You're all caught up.</div>
          ) : (
            items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              const color = COLORS[n.severity] || "#00E5FF";
              return (
                <button
                  key={n.notif_id}
                  onClick={() => { if (n.scan_id) { setOpen(false); navigate(`/report/${n.scan_id}`); } }}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors ${n.read ? "opacity-60" : ""}`}
                >
                  <Icon size={18} style={{ color }} className="shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm text-white font-medium">{n.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{n.body}</p>
                    <p className="text-[11px] text-slate-600 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
